import { api } from '@/utils/api';
import store from '@/redux/store';
import { setShowLoader } from '@/redux/slices/loaderSlice';

const DEFAULT_LIFETIME_MS = 7200 * 1000;
const REFRESH_THRESHOLD_MS = 5 * 60 * 1000;
const PROACTIVE_REFRESH_MS = 10 * 60 * 1000;

let accessToken = null;
let expirationTime = null;
let inFlightRequest = null;
let refreshTimer = null;

const decodeJwtExpiration = (token) => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(normalized));
    if (typeof decoded.exp === 'number') {
      return decoded.exp * 1000;
    }
    return null;
  } catch {
    return null;
  }
};

const scheduleProactiveRefresh = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  if (!expirationTime) return;
  const delay = expirationTime - Date.now() - PROACTIVE_REFRESH_MS;
  if (delay <= 0) return;
  refreshTimer = setTimeout(() => {
    MeteomaticsTokenService.fetchToken().catch(() => {});
  }, delay);
};

const storeToken = (token) => {
  accessToken = token;
  expirationTime = decodeJwtExpiration(token) ?? Date.now() + DEFAULT_LIFETIME_MS;
  scheduleProactiveRefresh();
};

const getStoredToken = () => accessToken;

const isValid = () => {
  if (!accessToken || !expirationTime) return false;
  return Date.now() < expirationTime - REFRESH_THRESHOLD_MS;
};

export class MeteomaticsTokenService {
  static async fetchToken() {
    if (inFlightRequest) {
      return inFlightRequest;
    }

    inFlightRequest = api
      .get('/security/meteomatics/token')
      .then((response) => {
        const token = response?.data?.access_token;
        if (!token) {
          throw new Error('Meteomatics token not received from BFF');
        }
        storeToken(token);
        return token;
      })
      .finally(() => {
        inFlightRequest = null;
        store.dispatch(setShowLoader(false));
      });

    return inFlightRequest;
  }

  static isTokenValid() {
    return isValid();
  }

  static getTokenSync() {
    return isValid() ? accessToken : null;
  }

  static async getValidToken() {
    if (isValid()) {
      return accessToken;
    }
    return this.fetchToken();
  }

  static getStoredToken() {
    return getStoredToken();
  }

  static clearTokens() {
    accessToken = null;
    expirationTime = null;
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  }

  static prefetch() {
    if (isValid() || inFlightRequest) return inFlightRequest ?? Promise.resolve(accessToken);
    return this.fetchToken().catch(() => null);
  }
}

export { storeToken, getStoredToken };

export default MeteomaticsTokenService;
